import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Star, Wrench, Heart } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Tools tab inside the course player.
 *
 * Lists course-level affiliate tools as cards. Required tools surface first.
 * Tracks click events on the red "Get X →" CTA.
 *
 * Props:
 *   - courseSlug
 *   - courseId — used for click tracking payload
 */
export default function CourseTools({ courseSlug, courseId }) {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const impressionsSent = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    api.get(`/courses/${courseSlug}/tools`).then((r) => {
      if (cancelled) return;
      setTools(r.data.tools || []);
    }).catch((e) => {
      console.error("Failed to load tools", e);
      toast.error("Couldn't load tools");
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [courseSlug]);

  // Fire impressions once per session for each tool when they're rendered
  useEffect(() => {
    tools.forEach((t) => {
      if (impressionsSent.current.has(t.id)) return;
      impressionsSent.current.add(t.id);
      api.post("/cta-events/impression", {
        kind: "tool",
        id: t.id,
        partner_id: t.partner_id,
        course_id: courseId,
      }).catch((e) => console.error("Tool impression failed", e));
    });
  }, [tools, courseId]);

  const handleClick = (t) => {
    api.post("/cta-events/click", {
      kind: "tool",
      id: t.id,
      partner_id: t.partner_id,
      course_id: courseId,
    }).catch((e) => console.error("Tool click failed", e));
  };

  if (loading) return <p className="text-gray-500 text-sm p-6">Loading tools…</p>;
  if (!tools.length) {
    return (
      <div className="bg-white border border-gray-200 p-8 text-center" data-testid="course-tools-empty">
        <Wrench className="text-fc-red mx-auto mb-3" size={24} />
        <p className="text-gray-600 text-sm">No tools have been added to this course yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="course-tools-tab">
      <div className="bg-white border border-gray-200 p-5 flex items-start gap-3">
        <Heart size={16} className="text-fc-red flex-shrink-0 mt-0.5" />
        <p className="text-gray-500 text-sm leading-relaxed">
          These are the tools the instructor uses in this course. Many include affiliate links — your support helps us keep the lights on.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {tools.map((t) => (
          <div
            key={t.id}
            className={`bg-white border ${t.is_required ? "border-fc-red/60" : "border-gray-200"} p-5 flex flex-col gap-4`}
            data-testid={`tool-card-${t.partner.slug}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-white p-2 flex-shrink-0">
                {t.partner.logo_url ? (
                  <img
                    src={t.partner.logo_url} alt={t.partner.name}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full bg-fc-red/20 flex items-center justify-center text-fc-red font-black text-xs">
                    {t.partner.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-black text-base leading-tight">{t.effective_label}</h3>
                  {t.is_required && (
                    <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase font-black text-fc-red bg-fc-red/15 px-1.5 py-0.5 rounded">
                      <Star size={9} /> REQUIRED
                    </span>
                  )}
                </div>
                <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">{t.partner.category}</p>
              </div>
            </div>

            <p className="text-gray-500 text-sm leading-relaxed flex-1">{t.effective_description}</p>

            <a
              href={t.effective_url}
              target="_blank" rel="noopener noreferrer sponsored"
              onClick={() => handleClick(t)}
              className="btn-fc-red w-full justify-center text-xs"
              data-testid={`tool-cta-${t.partner.slug}`}
            >
              Get {t.partner.name} <ExternalLink size={12} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
